import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './TourSteps.css';
import {  NewNextArrowIconEventPage, Previousicon } from '../../utils/icons';
import { toast } from 'react-toastify';

const TourSteps = ({ isActive, onComplete, tourSteps }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [popupPosition, setPopupPosition] = useState({ top: '50%', left: '50%', arrow: 'none' });
  const animationFrameRef = useRef(null);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      cleanup();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      onComplete();
    }
  };


  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    cleanup();
    toast.info('Tour Skipped');
    onComplete();
  };

  const cleanup = () => {
    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current);
    }
    document.querySelectorAll('.tour-highlight').forEach((el) => {
      el.classList.remove('tour-highlight');
    });
  };

  const calculatePopupPosition = (element, selector) => {
    if (!element) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', arrow: 'none' };

    const rect = element.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const popupHeight = 250;
    const popupWidth = 340;
    let top, left, transform, arrow;

    if (selector === '[data-tour="wellness-device"]' || 
    selector === '[data-tour="announcements"]' || 
    selector === '[data-tour="focus-cards"]' || 
    selector === '[data-tour="my-activities"]' ||
    selector === '[data-tour="workout-section"]') {
      top = rect.bottom + 30;
      left = rect.left + (rect.width / 2);
      transform = 'translateX(-50%)';
      arrow = 'top';
    } else if (selector === '[data-tour="monthly-theme"]') {
      top = rect.bottom + 20;
      left = rect.left + (rect.width / 2);
      transform = 'translateX(-50%)';
      arrow = 'top';
    } else if (selector === '[data-tour="wellness-tip"]' || 
               selector === '[data-tour="health-goals"]') {
      top = rect.top;
      left = rect.right + 20;
      transform = 'none';
      arrow = 'left';
    } else if (selector === '[data-tour="experience-points"]') {
      top = rect.top;
      left = rect.left - popupWidth - 80;
      transform = 'none';
      arrow = 'right';
    } else if (selector === '[data-tour="wellness-hub"]') {
      top = rect.top - popupHeight - 30;
      left = rect.left + (rect.width / 2);
      transform = 'translateX(-50%)';
      arrow = 'bottom';
    }

    if (left < 20) left = 20;
    if (left + popupWidth > windowWidth) left = windowWidth - popupWidth - 20;
    if (top < 20) top = 20;
    if (top + popupHeight > windowHeight) top = windowHeight - popupHeight - 20;

    return { top: `${top}px`, left: `${left}px`, transform, arrow };
  };

  useEffect(() => {
    if (!isActive || !tourSteps[currentStep]) return;

    cleanup();
    
    const selector = tourSteps[currentStep].selector;
    let retryCount = 0;
    const maxRetries = 20;
    
    const tryHighlight = () => {
      const element = document.querySelector(selector);
      
      if (element) {
        element.classList.add('tour-highlight');
        
        if (selector === '[data-tour="wellness-device"]') {
          const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: elementTop - 50, behavior: 'smooth' });
        } else {
          const scrollBlock = (selector === '[data-tour="announcements"]' || 
                    selector === '[data-tour="focus-cards"]' || 
                    selector === '[data-tour="my-activities"]' ||
                    selector === '[data-tour="workout-section"]') ? 'start' : 'center';
          element.scrollIntoView({ behavior: 'smooth', block: scrollBlock });
        }

        const updatePosition = () => {
          const el = document.querySelector(selector);
          if (el) {
            setPopupPosition(calculatePopupPosition(el, selector));
          }
        };

        const checkPosition = () => {
          updatePosition();
          animationFrameRef.current = window.requestAnimationFrame(checkPosition);
        };

        animationFrameRef.current = window.requestAnimationFrame(checkPosition);
      } else if (retryCount < maxRetries) {
        retryCount++;
        window.setTimeout(tryHighlight, 150);
      }
    };
    
    tryHighlight();

    return () => {
      if (!isActive) cleanup();
    };
  }, [currentStep, isActive, tourSteps]);

  if (!isActive) return null;

  const step = tourSteps[currentStep];

  return (
    <>
      <div className="tour-overlay" />
      
      <div 
        className={`tour-popup tour-arrow-${popupPosition.arrow}`}
        style={{
          top: popupPosition.top,
          left: popupPosition.left,
          transform: popupPosition.transform
        }}
      >
        <h3 className="tour-title">{step.title}</h3>
        <p className="tour-description">{step.description}</p>
        <div className="tour-counter">
          {currentStep + 1} of {tourSteps.length}
        </div>
        <div className="tour-actions">
          <div>
            {currentStep === 0 && (
              <button onClick={handleSkip} className="tour-button tour-button-skip">
                Skip Tour
              </button>
            )}
          </div>
          <div className="tour-button-group">
            {currentStep > 0 && (
              <button onClick={handlePrevious} className="tour-button tour-button-previous">
                {Previousicon()}
                Previous
              </button>
            )}
            <button onClick={handleNext} className="tour-button tour-button-next">
              {currentStep === tourSteps.length - 1 ? 'Get Started' : 'Next'}
              <span>{NewNextArrowIconEventPage('#fff')}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

TourSteps.propTypes = {
  isActive: PropTypes.bool.isRequired,
  onComplete: PropTypes.func.isRequired,
  tourSteps: PropTypes.array.isRequired
};

export default TourSteps;
